/**
 * @voltxampere/engine — Physical constants used by nonlinear stamps
 *
 * All values are in SI units. The thermal voltage `Vt = kT/q` is
 * the canonical knob: at the SPICE reference (T = 300.15 K, 27 °C)
 * it is `VT_27C ≈ 25.852 mV`; at runtime stamps consume
 * `vtAtTemperature(ctx.temperature)` so that
 * `circuit.simulationSettings.dc.temperature` (Sprint 80.Z L-AYARLAR-5)
 * actually shifts BJT g_m, diode knee, zener slope, and the Newton
 * trust-region uniformly.
 *
 * `VT_27C` is preserved as the 27 °C *reference* — kept for the few
 * places that explicitly need the SPICE-standard knee (regression
 * tests pinning the canonical 27 °C operating point, `pnjlim`'s
 * canonical small-signal example). Stamps reading the field
 * directly at temperature ≠ 27 °C would silently revert the L-5
 * lane's wire-through; new code MUST use `vtAtTemperature(T)`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Boltzmann constant (J/K). */
export const BOLTZMANN_K = 1.380649e-23;
/** Elementary charge (C). */
export const ELEMENTARY_CHARGE_Q = 1.602176634e-19;
/** Reference temperature (K) — 27 °C, SPECS §3.5 default. */
export const REFERENCE_TEMPERATURE_K = 300.15;
/**
 * Thermal voltage `Vt = kT/q` at 27 °C. Approximately 0.025852 V.
 * SPECS §3.5 quotes "≈ 25.85 mV at 27 °C"; the value here is
 * computed from the codata constants above so it stays exact to
 * full precision rather than truncated to the spec's rounded form.
 *
 * Equivalent to `vtAtTemperature(REFERENCE_TEMPERATURE_K)` —
 * IEEE-754-bit-identical because both expressions are the same
 * `(BOLTZMANN_K * 300.15) / ELEMENTARY_CHARGE_Q` Float64
 * multiplication/division pair.
 */
export const VT_27C = (BOLTZMANN_K * REFERENCE_TEMPERATURE_K) / ELEMENTARY_CHARGE_Q;
/**
 * Sprint 80.Z L-AYARLAR-5 — thermal voltage at arbitrary T (K).
 *
 * `Vt = kT/q`. Stamps that consume the runtime `ctx.temperature`
 * call this to pick the local Vt for diode/BJT/zener Shockley
 * exponents and for the Newton trust-region bound (Sprint 8a
 * delta-V damping uses `10·vt`).
 *
 * Engine zero-diff invariant carry-over: at the SPICE reference
 * `T = REFERENCE_TEMPERATURE_K = 300.15 K`, `vtAtTemperature(T)`
 * returns the exact same Float64 as `VT_27C` (IEEE-754
 * determinism), so existing sentinel tests stay bit-identical.
 *
 * Throws via the caller's contract if `T <= 0` (absolute zero is
 * unphysical for these stamps). The function itself returns
 * `NaN` / `Infinity` on non-finite input — callers are expected
 * to pass `circuit.simulationSettings.dc.temperature` which is
 * validated upstream by `DCSettingsSchema`.
 */
export function vtAtTemperature(temperatureK) {
    return (BOLTZMANN_K * temperatureK) / ELEMENTARY_CHARGE_Q;
}
//# sourceMappingURL=physical-constants.js.map